import { Divider, ListSubheader, MenuItem } from '@mui/material';
import { flatMap, groupBy } from 'lodash';
import { useEffect, useMemo, useRef } from 'react';
import { useController, useWatch } from 'react-hook-form';
import { RHFSelect } from 'src/components/hook-form';
import { LocationModel, LocationType } from 'src/gql/graphql';
import { flattenRecursiveTree } from 'src/modules/locations/utils';
import { useGetLocationsQuery } from 'src/services/locations';

type Props = Omit<Parameters<typeof RHFSelect>[0], 'children'> & {
  type: LocationType;
  parentName?: string;
  requiredParent?: boolean;
};

export function RHFSelectLocationField({
  type,
  requiredParent,
  parentName,
  ...rest
}: Props) {
  const prevParentId = useRef<string>();
  const parentId = useWatch({ name: parentName ?? '', disabled: !parentName });
  const { field } = useController({ name: rest.name });
  const { data: locations, isLoading } = useGetLocationsQuery();

  const filteredItems = useMemo(() => {
    const _locations = (locations?.getLocations ?? []) as LocationModel[];
    const flattenData = flattenRecursiveTree(_locations);

    const items = groupBy(
      flattenData.filter((item) => {
        if (item.type !== type) {
          return false;
        }

        if (!parentId) {
          return !requiredParent;
        }

        return item.parentId === parentId;
      }),
      (item) => item.parentNames?.[item.parentNames.length - 1] ?? ''
    );

    return items;
  }, [locations, parentId, requiredParent, type]);

  // Reset field value when parent changes
  useEffect(() => {
    // Skip on initial load or when parent id is the same
    if (isLoading || prevParentId.current === parentId) return;

    // Reset field value when parent changes
    if (prevParentId.current) {
      field.onChange('');
    }

    // Save previous parent id to compare
    prevParentId.current = parentId;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentId, isLoading]);

  const hasMultipleGroups = Object.keys(filteredItems).length > 1;

  return (
    <RHFSelect {...rest}>
      <MenuItem value="" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
        None
      </MenuItem>

      <Divider sx={{ borderStyle: 'dashed' }} />
      {flatMap(filteredItems, (items, group) => [
        hasMultipleGroups && <ListSubheader key={group}>{group}</ListSubheader>,
        ...items.map((item) => (
          <MenuItem key={`${item.id}-${group}`} value={item.id}>
            {item.name}
          </MenuItem>
        )),
      ])}
    </RHFSelect>
  );
}
